import { useEffect, useState } from 'react';

import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Col, Form } from 'antd';
import { Formik, FormikProps } from 'formik';
import { isEmpty } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { doClearUpdateAccountFinancialAssets, doUpdateAccountFinancialAssets } from 'src/actions';
import {
  AccountAnnualIncomeDto,
  AccountLiquidNetWorthDto,
  AccountTaxBracketDto,
  AccountTotalNetWorthDto,
  ErrorResponseDto,
} from 'src/dtos';
import { MAlert, MFormSaveButton, MFormSelect, UnknownErrorMessage } from 'src/lib';
import { Account } from 'src/models';
import { Spacing } from 'src/styles';
import { assertNonNullable } from 'src/utils';

import {
  ANNUAL_INCOME_OPTION_LIST,
  TOTAL_NET_WORTH_OPTION_LIST,
  LIQUID_NET_WORTH_OPTION_LIST,
  TAX_BRACKET_OPTION_LIST,
} from './optionList.constants';
import {
  ANNUAL_INCOME_TOOLTIP_TEXT,
  LIQUID_NET_WORTH_TOOLTIP_TEXT,
  TOTAL_NET_WORTH_TOOLTIP_TEXT,
} from './tooltips.constants';
import { upsertAccountHolderFinancialInformationAssetsValidation } from './validations';

export interface FinancialInformationAssetsFormValues {
  annualIncome?: AccountAnnualIncomeDto;
  totalNetWorth?: AccountTotalNetWorthDto;
  liquidNetWorth?: AccountLiquidNetWorthDto;
  taxBracket?: AccountTaxBracketDto;
}

export interface FinancialInformationAssetsFormProps {
  account?: Account;
  onCancel?: () => void;
  onSave?: () => void;
}

export const FinancialInformationAssetsForm = ({ account, onCancel, onSave }: FinancialInformationAssetsFormProps) => {
  const dispatch = useDispatch();

  const isUpdateLoading = useSelector((state: any) => Boolean(state.accounts.updateFinancialAssets.isLoading));
  const succeededUpdate = useSelector((state: any) => Boolean(state.accounts.updateFinancialAssets.__succeeded));
  const updateError: ErrorResponseDto | undefined = useSelector(
    (state: any) => state.accounts.updateFinancialAssets.error,
  );

  const [initialValues, setInitialValues] = useState<FinancialInformationAssetsFormValues>({});

  const isFinancialInformationAssetsAlreadySaved = () => !isEmpty(account?.financialAssets?.annualIncome);

  const _onCancel = (form: FormikProps<FinancialInformationAssetsFormValues>) => {
    form.resetForm();

    if (onCancel) {
      onCancel();
    }
  };

  const _onSave = (form: FormikProps<FinancialInformationAssetsFormValues>) => {
    form.submitForm();

    if (onSave) {
      onSave();
    }
  };

  useEffect(() => {
    setInitialValues({
      annualIncome: account?.financialAssets?.annualIncome?.value,
      liquidNetWorth: account?.financialAssets?.liquidNetWorth?.value,
      totalNetWorth: account?.financialAssets?.totalNetWorth?.value,
      taxBracket: account?.financialAssets?.taxBracket?.value,
    });
  }, [account?.primaryAccountHolder]);

  useEffect(() => {
    if (succeededUpdate) {
      if (onCancel) {
        onCancel();
      }
    }
  }, [succeededUpdate]);

  useEffect(() => {
    return () => {
      dispatch(doClearUpdateAccountFinancialAssets());
    };
  }, []);

  return (
    <Formik
      enableReinitialize
      validateOnChange
      validateOnBlur
      onSubmit={values => {
        assertNonNullable(account, 'Account');

        dispatch(
          doUpdateAccountFinancialAssets({
            params: {
              id: account.id,
            },
            body: values,
          }),
        );
      }}
      initialValues={initialValues}
      validationSchema={upsertAccountHolderFinancialInformationAssetsValidation}>
      {form => {
        return (
          <Form autoComplete='off' style={{ width: '100%' }}>
            <Col span={24}>
              <MFormSelect
                label='Annual Income'
                placeholder='Select'
                defaultValue={account?.financialAssets?.annualIncome?.value}
                options={ANNUAL_INCOME_OPTION_LIST}
                tooltip={{
                  type: 'popover',
                  content: ANNUAL_INCOME_TOOLTIP_TEXT,
                  icon: 'info',
                }}
                error={form.errors.annualIncome}
                onChange={value => {
                  form.setFieldValue('annualIncome', value);
                }}
                name='financial-assets-annual-name-attribute'
                autoComplete='off'
                testId='account-annual-income'
              />
            </Col>
            <Col span={24}>
              <MFormSelect
                label='Total Net Worth'
                options={TOTAL_NET_WORTH_OPTION_LIST}
                defaultValue={account?.financialAssets?.totalNetWorth?.value}
                tooltip={{
                  type: 'popover',
                  content: TOTAL_NET_WORTH_TOOLTIP_TEXT,
                  icon: 'info',
                }}
                error={form.errors.totalNetWorth}
                onChange={value => {
                  form.setFieldValue('totalNetWorth', value);
                }}
                name='financial-assets-total-net-worth-attribute'
                autoComplete='off'
                testId='account-total-net-worth'
              />
            </Col>
            <Col span={24}>
              <MFormSelect
                label='Liquid Net Worth'
                options={LIQUID_NET_WORTH_OPTION_LIST}
                defaultValue={account?.financialAssets?.liquidNetWorth?.value}
                tooltip={{
                  type: 'popover',
                  content: LIQUID_NET_WORTH_TOOLTIP_TEXT,
                  icon: 'info',
                }}
                error={form.errors.liquidNetWorth}
                onChange={value => {
                  form.setFieldValue('liquidNetWorth', value);
                }}
                name='financial-assets-liquid-net-worth-attribute'
                autoComplete='off'
                testId='account-liquid-net-worth'
              />
            </Col>
            <Col span={24}>
              <MFormSelect
                label='Tax Bracket'
                options={TAX_BRACKET_OPTION_LIST}
                defaultValue={account?.financialAssets?.taxBracket?.value}
                error={form.errors.taxBracket}
                onChange={value => {
                  form.setFieldValue('taxBracket', value);
                }}
                name='financial-assets-tax-bracket-attribute'
                autoComplete='off'
                testId='account-tax-bracket'
              />
            </Col>
            {updateError && (
              <Col span={24}>
                <MAlert
                  type='error'
                  description={<UnknownErrorMessage message={updateError?.message} />}
                  showIcon
                  icon={<ExclamationCircleOutlined />}
                  closable={false}
                  className={Spacing.my16}
                />
              </Col>
            )}
            <Col span={24}>
              <MFormSaveButton<FinancialInformationAssetsFormValues>
                loading={isUpdateLoading}
                onCancel={_onCancel}
                onSave={_onSave}
                isEditMode={isFinancialInformationAssetsAlreadySaved()}
                form={form}
                testId='income-and-assets'
              />
            </Col>
          </Form>
        );
      }}
    </Formik>
  );
};
